/**
 * FunnelHealthWidget — MA-FUNNEL-HEALTH-SCORE-013
 * 
 * War Room widget showing aggregate funnel health across all funnels.
 * Displays: average score, bucket counts, lowest-scoring funnels.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, ShieldCheck, ShieldAlert, ShieldX,
  ChevronRight, AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api } from '@/lib/api';

const STATUS_CONFIG = {
  healthy:   { icon: ShieldCheck, color: 'text-emerald-400', bg: 'bg-emerald-500/15', ring: 'ring-emerald-500/25' },
  good:      { icon: Shield,      color: 'text-sky-400',     bg: 'bg-sky-500/15',     ring: 'ring-sky-500/25' },
  at_risk:   { icon: ShieldAlert, color: 'text-amber-400',   bg: 'bg-amber-500/15',   ring: 'ring-amber-500/25' },
  unhealthy: { icon: ShieldX,     color: 'text-red-400',     bg: 'bg-red-500/15',     ring: 'ring-red-500/25' },
};

function ScorePill({ score, status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.good;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ring-1 ${cfg.bg} ${cfg.color} ${cfg.ring} tabular-nums`}>
      {score}
    </span>
  );
}

function BucketDot({ count, status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.good;
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${cfg.bg} ring-1 ${cfg.ring}`} />
      <span className="text-xs text-slate-400">{count}</span>
    </div>
  );
}

export function FunnelHealthWidget({ tenantId }) {
  const navigate = useNavigate();
  
  const { data, isLoading } = useQuery({
    queryKey: ['funnel-health-overview', tenantId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (tenantId) params.append('tenant_id', tenantId);
      // MA-RBAC-BATCH2-FUNNELS-ADVERTISING-106B: Changed to War Room wrapper endpoint
      const res = await api.get(`/war-room/funnel-health-overview?${params}`);
      return res.data;
    },
    staleTime: 60000,
  });
  
  if (isLoading) {
    return (
      <Card data-testid="funnel-health-widget" className="border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-500" />
            Funnel Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-8 bg-slate-700/50 rounded w-24" />
            <div className="h-4 bg-slate-700/50 rounded w-40" />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!data || data.total_funnels === 0) {
    return (
      <Card data-testid="funnel-health-widget" className="border-slate-700/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-500" />
            Funnel Health
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500">No funnels to evaluate</p>
        </CardContent>
      </Card>
    );
  }
  
  const avgColor = data.average_score >= 90 ? 'text-emerald-400' :
                   data.average_score >= 75 ? 'text-sky-400' :
                   data.average_score >= 60 ? 'text-amber-400' : 'text-red-400';
  
  return (
    <Card data-testid="funnel-health-widget" className="border-slate-700/50">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-slate-300 flex items-center gap-2">
          <Shield className="h-4 w-4 text-slate-500" />
          Funnel Health
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Average score */}
        <div className="flex items-baseline gap-2">
          <span data-testid="health-avg-score" className={`text-3xl font-bold tabular-nums ${avgColor}`}>
            {Math.round(data.average_score)}
          </span>
          <span className="text-sm text-slate-500">avg across {data.total_funnels} funnels</span>
        </div>
        
        {/* Bucket summary */}
        <div className="flex items-center gap-4">
          <BucketDot count={data.healthy_count} status="healthy" />
          <BucketDot count={data.good_count} status="good" />
          <BucketDot count={data.at_risk_count} status="at_risk" />
          <BucketDot count={data.unhealthy_count} status="unhealthy" />
        </div>
        
        {/* Needs review banner */}
        {data.needs_review_count > 0 && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-amber-500/10 ring-1 ring-amber-500/20">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span data-testid="health-review-count" className="text-xs text-amber-300">
              {data.needs_review_count} funnel{data.needs_review_count !== 1 ? 's' : ''} need review
            </span>
          </div>
        )}
        
        {/* Lowest funnels */}
        {data.lowest_funnels?.length > 0 && (
          <div className="space-y-1">
            <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Lowest Scoring</h4>
            {data.lowest_funnels.slice(0, 3).map(f => (
              <button
                key={f.funnel_id}
                data-testid={`health-funnel-${f.funnel_id}`}
                onClick={() => navigate(`/app/funnels/${f.funnel_id}/builder`)}
                className="w-full flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-slate-700/30 transition-colors group"
              >
                <span className="text-xs text-slate-300 truncate max-w-[60%]">{f.funnel_name}</span>
                <div className="flex items-center gap-1.5">
                  <ScorePill score={f.health_score} status={f.health_status} />
                  <ChevronRight className="h-3 w-3 text-slate-600 group-hover:text-slate-400 transition-colors" />
                </div>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FunnelHealthWidget;
